import * as THREE from "three";
import { MATS } from "../materials.js";
import * as H from "./spec.js";
import { groundY } from "../spec.js";

/**
 * The side route to the back door: concrete steps down off the open left end
 * of the porch (porch.js removed the railing there), a paver path along the
 * -X side of the house, and a low timber gate fence across the passage.
 *
 * The passage runs between the house wall (x -5.75) and the lot boundary
 * line (x -6.75).  The route the cat gets:
 *
 *   deck 0.55 -> tread 0.367 -> tread 0.184 -> grade   (0.183 risers, exactly
 *   the front steps' rise -- inside the cat's 0.23 m step, so walked, same as
 *   them.  Written as hops when the step-up was 0.13; the number moved, the
 *   treads did not.)
 *
 * THE PASSAGE IS A RAMP.  It used to be level ground with the whole 0.80 m
 * drop happening at a retaining wall behind the house; the block now slopes
 * from the front elevation to the back wall (spec.terraceY), and this path
 * runs 11.85 m straight down it.  Laid flat it hung 0.78 m in the air by the
 * back gate.  The paver field and its border courses are therefore ribbons
 * that follow groundY rather than slabs, and every rigid assembly -- the
 * landing, the fixed panel, the gate leaf -- takes ONE level of its own,
 * because a gate whose boards each sat on their own patch of slope would
 * read as a broken gate rather than a gate on a hill.
 *
 * THE GATE NEVER SEALS THE PASSAGE.  The leaf is modelled standing open,
 * swung back to lie along the lot boundary line, and its collider is built
 * in that swung-back position.  The clear gap at ground level between the
 * open leaf and the fixed panel is ~0.87 m -- far over the 0.35 m a 0.24 m
 * cat needs -- and it is exactly where the path runs.
 */

const WALL = H.X.left;                     // -5.75, the house's -X face
const DECK = H.PORCH.deck;                 // 0.55
const RISE = DECK / H.PORCH.steps;         // 0.1833, matches the front flight

// The porch opening is z 1.79..2.62 (wall pilaster to the return's newel);
// the flight is centred on it and a touch wider, like the front steps.
const STEP_Z = 2.20;
const STEP_W = 0.90;

const PATH_X = -6.20;                      // path centre: spans -6.60..-5.80,
const PATH_W = 0.80;                       // the passage is only 1.0 m wide now
const PATH_END = -10.15;                   // stops short of fences.js's side
                                           // gate at z -10.45

const GATE_Z = -2.0;                       // a little way down the passage
const POST_B = -5.80;                      // fixed panel's gate-side post
const HINGE_X = -7.40;                     // hinge post by the lot line
const LEAF_X = -6.5975;                    // leaf plane, swung back along z

export function build(b) {
  steps(b);
  path(b);
  gateFence(b);

  b.mark("sidepath.tread1", -5.95, RISE, STEP_Z);
  b.mark("sidepath.tread2", -5.65, RISE * 2, STEP_Z);
  b.mark("sidepath.gate", -6.85, groundY(-6.85, GATE_Z), GATE_Z);   // centre of the open gap
}

// ---------------------------------------------------------------------------
/**
 * Two projecting treads down the deck's west edge; the deck edge itself is
 * the third riser.  Same construction as the front flight: bodies solid:false
 * so each collider top matches the *nosing* surface, not the body 3 cm below.
 */
function steps(b) {
  const dark = MATS.concreteDark;
  b.block(-5.65, 0, STEP_Z, 0.30, RISE * 2 - 0.032, STEP_W, dark, { solid: false });
  b.block(-5.95, 0, STEP_Z, 0.30, RISE - 0.032, STEP_W, dark, { solid: false });
  // Nosings: 3 cm concrete caps with a 25 mm lip on the downhill (-X) side.
  b.block(-5.6625, RISE * 2 - 0.032, STEP_Z, 0.335, 0.032, STEP_W + 0.04,
          MATS.concrete, { solid: false });
  b.block(-5.9625, RISE - 0.032, STEP_Z, 0.335, 0.032, STEP_W + 0.04,
          MATS.concrete, { solid: false });
  b.collider(-5.65, 0, STEP_Z, 0.30, RISE * 2, STEP_W);   // tread top 0.367
  b.collider(-5.95, 0, STEP_Z, 0.30, RISE, STEP_W);       // tread top 0.184
}

/**
 * Ground decals only -- no colliders, the cat walks on grade.  Tops sit a
 * couple of centimetres proud of the lawn (0.025), and every layer gets its
 * own height so nothing is coplanar.
 */
function path(b) {
  const Y0 = 0.026, TH = 0.024;            // paver field tops at 0.050

  // landing at the foot of the flight, a course wider than the path.  Level:
  // it is the bottom of a flight of steps, and the ground under it has not
  // started to fall yet (z 2.20 is north of the ramp's crest).
  b.slab(-6.70, Y0 + groundY(-6.70, STEP_Z), STEP_Z, 1.30, 1.00, MATS.paver,
         { thickness: TH, tile: 0.8 });

  // The run back along the house, abutting the landing edge-to-edge.  A
  // ribbon, not a slab: over 11.85 m the ramp falls 0.78 m, so one box placed
  // at its own middle would bury its head and fly its tail by 0.39 m each.
  ribbon(b, PATH_X, PATH_W, 1.70, PATH_END, Y0 + TH, MATS.paver, 0.8);
  for (const s of [-1, 1]) {
    ribbon(b, PATH_X + s * (PATH_W / 2 - 0.045), 0.06, 1.70, PATH_END,
           0.038 + 0.014, MATS.edgePaver, 0.4);
  }

  // Cross joints are 3 cm of z each -- far too short for the slope to show
  // across one, so they stay slabs and simply take the ground they land on.
  for (let z = 1.25; z > PATH_END + 0.2; z -= 0.78) {
    b.slab(PATH_X, 0.040 + groundY(PATH_X, z), z, PATH_W - 0.18, 0.03,
           MATS.concreteDark, { thickness: 0.012 });
  }
}

/**
 * A flat-topped strip of paving that follows the ground down the passage.
 *
 * Sampled every STATION metres and emitted as quads whose corners sit on
 * groundY, so the surface is continuous instead of stepped and there is no
 * height at which it is wrong.  `top` is the height of the finished surface
 * above the ground, which is what every caller actually knows.
 *
 * Two triangles per station and no sides: this is a decal on grass, the cat
 * walks on grade over it (no colliders here at all), and the 2 cm edge it
 * would need is not visible against the lawn from any angle the camera
 * reaches.
 */
const STATION = 0.5;
function ribbon(b, xc, w, z0, z1, top, mat, tile) {
  const x0 = xc - w / 2, x1 = xc + w / 2;
  const n = Math.max(1, Math.ceil(Math.abs(z1 - z0) / STATION));
  const pos = [];
  for (let i = 0; i < n; i++) {
    const za = z0 + ((z1 - z0) * i) / n;
    const zb = z0 + ((z1 - z0) * (i + 1)) / n;
    const ya0 = groundY(x0, za) + top, ya1 = groundY(x1, za) + top;
    const yb0 = groundY(x0, zb) + top, yb1 = groundY(x1, zb) + top;
    pos.push(x0, ya0, za,  x1, ya1, za,  x1, yb1, zb,
             x0, ya0, za,  x1, yb1, zb,  x0, yb0, zb);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  const nrm = new Float32Array(pos.length);
  for (let i = 1; i < nrm.length; i += 3) nrm[i] = 1;
  g.setAttribute("normal", new THREE.BufferAttribute(nrm, 3));
  b.add(g, mat, { collider: null, ao: 0, tile });
}

/**
 * A low timber fence panel from the house wall partway across the passage,
 * and the gate leaf that would close the rest -- shown standing OPEN, folded
 * back along the lot boundary line, the way someone heading for the back
 * door left it.  Colliders: one per fixed run (never per board), one for the
 * hinge post, and one for the leaf in its swung-back position.  Nothing
 * spans the ~0.87 m opening the path threads through.
 */
function gateFence(b) {
  const F = MATS.fence, T = MATS.timber, M = MATS.metalDark;
  // One level for the whole assembly, and taken at the MIDDLE of it rather
  // than at the gate line.  The leaf hangs off the hinge post, so it cannot
  // take a level of its own without the straps parting company with the post
  // they are bolted to -- but the assembly reaches from the fixed panel at
  // z -2.0 to the open leaf's free end at -2.98, and the ramp falls 0.08 m
  // across that.  Levelled at the gate line the free end flew 0.136 m, over
  // the 0.10 m at which fences.js reckons a gap stops reading as a gap and
  // starts reading as broken.  Levelled at the middle it is 0.099 m at the
  // free end and 0.037 m of bury at the panel, both inside that budget.
  const GY = groundY(HINGE_X, (GATE_Z + -2.98) / 2);

  // ---- fixed panel, wall post to gate post --------------------------------
  b.block(-5.79, GY, GATE_Z, 0.09, 1.12, 0.09, T, { solid: false });
  b.block(POST_B, GY, GATE_Z, 0.10, 1.14, 0.10, T, { solid: false });
  b.block(POST_B, 1.14 + GY, GATE_Z, 0.14, 0.03, 0.14, T, { solid: false });
  for (const ry of [0.16, 0.76]) {
    b.block(-6.0675, ry + GY, GATE_Z, 0.465, 0.07, 0.05, T, { solid: false });
  }
  for (let i = 0; i < 4; i++) {
    b.block(-6.24 + i * 0.12, 0.05 + GY, GATE_Z + 0.04, 0.09, 0.97, 0.024, F,
            { solid: false });
  }
  b.block(-6.0675, 1.02 + GY, GATE_Z, 0.465, 0.045, 0.08, T, { solid: false });
  // catch plate the latch would drop into, facing the opening
  b.block(-6.42, 0.68 + GY, GATE_Z, 0.05, 0.10, 0.06, M, { solid: false });
  b.collider(-6.075, GY, GATE_Z, 0.65, 1.14, 0.13);       // one box, whole run

  // ---- hinge post on the lot-boundary side --------------------------------
  b.block(HINGE_X, GY, GATE_Z, 0.10, 1.18, 0.10, T, { solid: false });
  b.block(HINGE_X, 1.18 + GY, GATE_Z, 0.14, 0.03, 0.14, T, { solid: false });
  b.collider(HINGE_X, GY, GATE_Z, 0.12, 1.20, 0.12);

  // ---- the leaf, swung back ~95 degrees to lie along z --------------------
  // Ledged and braced: boards on the outside face, two rails and the
  // diagonal on the inside, hinge straps at the post end, latch at the free
  // end.  Runs z -2.06..-2.98; its collider is HERE, not across the path.
  for (let i = 0; i < 8; i++) {
    b.block(LEAF_X - 0.012, 0.06 + GY, -2.1075 - i * 0.115, 0.024, 0.94, 0.095, F,
            { solid: false });
  }
  for (const ry of [0.16, 0.74]) {
    b.block(-7.305, ry + GY, -2.51, 0.045, 0.07, 0.88, T, { solid: false });
  }
  // diagonal brace, low at the hinge end rising to the latch end
  const brace = new THREE.BoxGeometry(0.045, 0.07, 0.97);
  brace.rotateX(0.556);
  brace.translate(-7.305, 0.485 + GY, -2.51);
  b.add(brace, T, { tile: 0 });
  // hinge straps and latch bar
  for (const hy of [0.30, 0.83]) {
    b.block(-7.30, hy + GY, -2.17, 0.05, 0.045, 0.20, M, { solid: false });
  }
  b.block(-7.30, 0.70 + GY, -2.90, 0.05, 0.05, 0.12, M, { solid: false });
  // the leaf's collider, in the swung-back position it is drawn in
  b.collider(LEAF_X, GY, -2.52, 0.11, 1.05, 1.00);
}
